import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2, HandHeart } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const ROLE_LABELS = {
  setup: 'Setup',
  kitchen: 'Kitchen',
  'check-in': 'Check-In',
  cleanup: 'Cleanup',
  transport: 'Transport',
  other: 'Other',
  unassigned: 'Unassigned',
};

const VolunteersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVolunteers = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/volunteers`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch {
      toast.error('Failed to load volunteers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVolunteers(); }, [fetchVolunteers]);

  const setRole = async (attendee, role) => {
    try {
      await axios.put(`${BACKEND_URL}/api/retreat/volunteers/${attendee.id}/role`,
        { role: role === 'unassigned' ? null : role },
        { headers: getAuthHeaders() });
      toast.success(`${attendee.first_name} → ${ROLE_LABELS[role] || role}`);
      fetchVolunteers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set role');
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center h-[50vh]"><Loader2 className="w-6 h-6 text-gray-300 animate-spin" /></div>;
  }

  const roleKeys = [...data.roles, 'unassigned'];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="volunteers-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Volunteers</h1>
        <p className="text-gray-600 mt-1">{data.volunteers.length} attendees said they're willing to serve — assign them to roles</p>
      </div>

      {/* Role counts */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="volunteer-counts">
        {roleKeys.map((r) => (
          <div key={r} className={`rounded-lg p-3 text-center border ${r === 'unassigned' ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
            <p className="text-2xl font-bold text-gray-900">{data.counts[r] || 0}</p>
            <p className="text-xs uppercase tracking-wide text-gray-500">{ROLE_LABELS[r]}</p>
          </div>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          {data.volunteers.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <HandHeart className="w-8 h-8 mx-auto mb-3 text-gray-200" />
              Nobody has volunteered yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="volunteers-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Phone</th>
                    <th className="py-2">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {data.volunteers.map((a) => (
                    <tr key={a.id} className="border-b border-gray-50">
                      <td className="py-2.5 pr-4 font-medium text-gray-900">{a.first_name} {a.last_name}</td>
                      <td className="py-2.5 pr-4 text-gray-500">{a.email}</td>
                      <td className="py-2.5 pr-4 text-gray-500">{a.phone || '—'}</td>
                      <td className="py-2.5">
                        <Select value={a.volunteer_role || 'unassigned'} onValueChange={(v) => setRole(a, v)}>
                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`volunteer-role-${a.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {roleKeys.map((r) => (
                              <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VolunteersPage;
